import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
// @mui
import { styled, alpha } from "@mui/material/styles";
import {
  Box,
  Link,
  Drawer,
  Typography,
  Avatar,
  Stack,
  IconButton,
} from "@mui/material";
// hooks
import useResponsive from "../../../hooks/useResponsive";
// components
import Logo from "../../../components/logo";
import Scrollbar from "../../../components/scrollbar";
import NavSection from "../../../components/nav-section";
import BackgroundImage from "../../../img/backgroundBlack.jpg";
import { useGetTenantPlanQuery } from "../../../features/tenants/tenantsApiSlice";
import {
  selectCurrentUser,
  selectCurrentUserData,
} from "../../../features/auth/authSlice";
import { useSelector } from "react-redux";
import { serverURL } from "../../../app/api/axios/http.service";
import useAuth from "../../../hooks/useAuth";
import UserRolesBadge from "../../../components/rolesbadge/UserRolesBadge";
import Iconify from "../../../components/iconify/Iconify";

//
// ----------------------------------------------------------------------

const NAV_WIDTH = 280;

const StyledAccount = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const openedMixin = (theme) => ({
  width: NAV_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DesktopDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: NAV_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));
// ----------------------------------------------------------------------

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
  onOpenNav: PropTypes.func,
};

export default function Nav({ openNav, onOpenNav, onCloseNav }) {
  const { pathname } = useLocation();
  const currentUserData = useSelector(selectCurrentUserData);
  const currentUserMail = useSelector(selectCurrentUser);
  const { status } = useAuth();
  const { data: tenantInfo } = useGetTenantPlanQuery();
  const [tenantLogoVersion, setTenantLogoVersion] = useState(() => {
    if (typeof window === "undefined") return "0";
    const storedVersion = window.localStorage.getItem("tenant_logo_version");
    return storedVersion || "0";
  });

  const isDesktop = useResponsive("up", "lg");

  const tenantHomepage = useMemo(() => {
    const homepage = tenantInfo?.homepage?.trim();
    return homepage || "";
  }, [tenantInfo]);

  const tenantLogoUrl = useMemo(() => {
    const tenantId = tenantInfo?._id || currentUserData?.tenant;
    if (!tenantId) return "";
    return `${serverURL()}/public/tenant/image/${tenantId}?v=${tenantLogoVersion}`;
  }, [currentUserData, tenantInfo, tenantLogoVersion]);

  const profileImageUrl = useMemo(() => {
    if (!currentUserData?.id || !currentUserData?.profileImagePath) return "";
    return `${serverURL()}/public/user/image/${currentUserData.id}`;
  }, [currentUserData]);

  const displayName =
    currentUserData?.firstName && currentUserData?.lastName
      ? `${currentUserData.firstName} ${currentUserData.lastName}`
      : currentUserMail || "Benutzerkonto";
  const initials = displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  useEffect(() => {
    if (openNav && !isDesktop) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleTenantLogoUpdated = (event) => {
      const nextVersion = event?.detail?.version
        ? String(event.detail.version)
        : String(Date.now());
      setTenantLogoVersion(nextVersion);
      window.localStorage.setItem("tenant_logo_version", nextVersion);
    };

    const handleStorageChange = (event) => {
      if (event.key !== "tenant_logo_version") return;
      if (!event.newValue) return;
      setTenantLogoVersion(event.newValue);
    };

    window.addEventListener("tenant-logo-updated", handleTenantLogoUpdated);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener(
        "tenant-logo-updated",
        handleTenantLogoUpdated,
      );
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        "& .simplebar-content": {
          height: 1,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {openNav && (
        <>
          <Box sx={{ mt: 2, mb: 4, mx: 1 }}>
            <Link underline="none">
              <StyledAccount>
                <Avatar
                  src={profileImageUrl}
                  alt="Profilbild"
                  children={!profileImageUrl ? initials : undefined}
                />

                <Box sx={{ ml: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: "white" }}>
                    {displayName}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 0.5 }}
                  >
                    <UserRolesBadge status={status} />
                  </Stack>
                </Box>
              </StyledAccount>
            </Link>
          </Box>
        </>
      )}
      <NavSection />

      <Box sx={{ flexGrow: 1 }} />

      {openNav && (
        <>
          <Box sx={{ px: 2.5, pb: 0, mt: 0 }}>
            <Stack
              alignItems="center"
              spacing={3}
              sx={{ pb: 2, borderRadius: 2 }}
            >
              {tenantHomepage ? (
                <Link
                  href={tenantHomepage}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  sx={{ display: "inline-flex" }}
                >
                  <Box
                    component="img"
                    src={tenantLogoUrl}
                    alt="Tenant Logo"
                    sx={{
                      width: 70,
                      cursor: "pointer",
                      borderRadius: "15px",
                    }}
                  />
                </Link>
              ) : (
                <Box
                  component="img"
                  src={tenantLogoUrl}
                  alt="Tenant Logo"
                  sx={{
                    width: 70,
                    borderRadius: "15px",
                  }}
                />
              )}
            </Stack>
          </Box>
        </>
      )}
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        // width: { lg: NAV_WIDTH },
      }}
    >
      {isDesktop ? (
        <DesktopDrawer
          variant="permanent"
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              backgroundImage: `url(${BackgroundImage})`,
              borderRightStyle: "dashed",
            },
          }}
        >
          <DrawerHeader>
            {openNav ? (
              <>
                <Box sx={{ py: 3, display: "inline-flex" }}>
                  <Logo />
                </Box>

                <IconButton onClick={onCloseNav}>
                  {<Iconify icon="material-symbols:chevron-left" />}
                </IconButton>
              </>
            ) : (
              <IconButton onClick={onOpenNav}>
                {<Iconify icon="material-symbols:chevron-right" />}
              </IconButton>
            )}
          </DrawerHeader>
          {renderContent}
        </DesktopDrawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              backgroundImage: `url(${BackgroundImage})`,
              width: NAV_WIDTH,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
